<script setup lang="ts">
import { TestimonialContent } from './types'
import EditableField from '@app/components/EditableField.vue'
import ImageUploader from '@components/customer_page/ImageUploader.vue'
import { computed } from 'vue'
import { inject } from 'vue'
import { Alignment } from '../types'

interface Props {
  editable: boolean
  position: number
  testimonial?: TestimonialContent
  textColor?: string
  alignment?: Alignment
}

const props = withDefaults(defineProps<Props>(), {
  alignment: 'left',
  textColor: undefined,
  editable: false,
  testimonial: () => ({
    attribution_line_1: '',
    attribution_line_2: '',
    attribution_line_3: '',
    author_name: '',
    author_title: '',
    author_company: '',
    avatar_url: '',
    testimonial_text: '',
  }),
})

const image = computed(() => {
  return props.testimonial?.image?.blob_url ?? props.testimonial.avatar_url
})

const headshotAlignment = computed(() => {
  switch (props.alignment) {
    case 'right':
      return 'justify-content-end'
    case 'center':
      return 'justify-content-center'
    default:
      return 'justify-content-start'
  }
})

const cssVariables = computed(() => {
  return {
    '--author-text-color': props.textColor,
  }
})

const onImageChange: (file: File | null, position: number) => void = inject('onImageChange')
</script>

<template>
  <div class="testimonial-slide d-flex flex-column justify-content-between" :style="cssVariables">
    <div class="testimonial-quote font-weight-bold mb-4" :class="`text-${alignment}`">
      <EditableField
        v-if="editable"
        :editable="false"
        :value="testimonial.testimonial_text"
        :quoted="true"
        placeholder="Testimonial Text"
      />
      <p v-else>
        <q>
          {{ testimonial.testimonial_text }}
        </q>
      </p>
    </div>
    <div class="testimonial-headshot d-flex align-items-center" :class="headshotAlignment">
      <div class="headshot" :class="{ 'mr-4': testimonial.avatar_url || editable }">
        <ImageUploader
          v-if="testimonial.avatar_url || editable"
          height="100px"
          variant="rounded"
          width="100px"
          :editable="editable"
          :image="image"
          @fileAdded="onImageChange($event, position)"
          @fileRemoved="onImageChange(null, position)"
        >
          <template #uploadButton>
            <span class="font-weight-bold">Add image</span>
          </template>
        </ImageUploader>
      </div>
      <div class="author">
        <EditableField
          :editable="false"
          :value="testimonial.attribution_line_1 || testimonial.author_name"
          class="author-name font-weight-bold fs-lg mb-2"
          placeholder="Author Name"
        />
        <EditableField
          class="font-weight-bold author-text"
          placeholder="Author Title"
          :editable="false"
          :value="testimonial.attribution_line_2 || testimonial.author_title || ''"
        />
        <EditableField
          class="author-text"
          placeholder="Author Company"
          :editable="false"
          :value="testimonial.attribution_line_3 || testimonial.author_company"
        />
      </div>
    </div>
  </div>
</template>

<style lang="sass">
.testimonial-slide
  border-radius: 0.5rem
  gap: 1rem
  flex-wrap: wrap
  @media (min-width: 768px)
    height: 100%
    flex-wrap: nowrap

.author
  color: var(--author-text-color)

  .author-text
    color: var(--author-text-color, #6C747D)
</style>
